import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "CobrosSuscripciones";

const RUTAS_FRONTEND = {
  inicio: "/inicio",
  nuevo: "/cobros/configuracion/nueva",
  editar: (id) => `/cobros/configuracion/edit/${id}`,
};

/* =========================================================
   UTILIDADES
========================================================= */

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const leerStorageJSON = (clave) => {
  try {
    const valor = localStorage.getItem(clave);
    return valor ? JSON.parse(valor) : null;
  } catch (error) {
    console.error(`No se pudo leer ${clave}:`, error);
    return null;
  }
};

const obtenerUsuarioStorage = () =>
  leerStorageJSON("usuario") ||
  leerStorageJSON("user") ||
  leerStorageJSON("usuarios") ||
  {};

const obtenerModulosStorage = () => {
  const permisos = leerStorageJSON("permisos");
  const usuario = obtenerUsuarioStorage();

  const modulos = Array.isArray(permisos)
    ? permisos
    : permisos?.modulos ||
      permisos?.permisos ||
      usuario?.modulos ||
      usuario?.permisos ||
      usuario?.usuario?.modulos ||
      usuario?.usuario?.permisos ||
      usuario?.user?.modulos ||
      usuario?.user?.permisos ||
      [];

  return Array.isArray(modulos) ? modulos : [];
};

const esAdministrador = () => {
  const usuario = obtenerUsuarioStorage();

  const rol =
    usuario?.rolObjectId?.nombreRol ||
    usuario?.rol?.nombreRol ||
    usuario?.rol?.nombre ||
    usuario?.rol ||
    usuario?.role ||
    usuario?.tipoUsuario ||
    usuario?.nombreRol ||
    usuario?.usuario?.rolObjectId?.nombreRol ||
    usuario?.usuario?.rol?.nombreRol ||
    usuario?.usuario?.rol?.nombre ||
    usuario?.usuario?.rol ||
    usuario?.user?.rolObjectId?.nombreRol ||
    usuario?.user?.rol?.nombreRol ||
    usuario?.user?.rol?.nombre ||
    usuario?.user?.rol ||
    "";

  const rolNormalizado = normalizarTexto(rol);

  return (
    rolNormalizado.includes("superadmin") ||
    rolNormalizado.includes("administrador") ||
    rolNormalizado === "admin"
  );
};

const obtenerModuloPorNombre = (nombreModulo) => {
  const moduloBuscado = normalizarTexto(nombreModulo);

  return (
    obtenerModulosStorage().find((modulo) => {
      const nombre =
        modulo?.nombreModulo ||
        modulo?.modulo ||
        modulo?.moduloNombre ||
        modulo?.nombre ||
        modulo?.name ||
        modulo?.moduloObjectId?.nombreModulo ||
        modulo?.moduloObjectId?.nombre ||
        "";

      return normalizarTexto(nombre) === moduloBuscado;
    }) || {}
  );
};

const tienePermiso = (modulo, accion) => {
  /*
   * SUPERADMIN / ADMINISTRADOR:
   * se consideran usuarios administrativos del sistema.
   */
  if (esAdministrador()) {
    return true;
  }

  const accionNormalizada = normalizarTexto(accion);

  const camposPorAccion = {
    leer: ["puedeLeer", "leer", "read", "ver", "listar"],
    crear: ["puedeCrear", "crear", "create", "add", "registrar"],
    actualizar: [
      "puedeActualizar",
      "actualizar",
      "update",
      "editar",
      "edit",
    ],
    eliminar: [
      "puedeEliminar",
      "eliminar",
      "delete",
      "borrar",
      "remove",
    ],
  };

  const campos =
    camposPorAccion[accionNormalizada] || [accion];

  const permisoDirecto = campos.some(
    (campo) =>
      modulo?.[campo] === true ||
      modulo?.acciones?.[campo] === true ||
      modulo?.permisos?.[campo] === true
  );

  const permisosEnArreglo = [
    ...(Array.isArray(modulo?.acciones)
      ? modulo.acciones
      : []),

    ...(Array.isArray(modulo?.permisos)
      ? modulo.permisos
      : []),

    ...(Array.isArray(modulo?.operaciones)
      ? modulo.operaciones
      : []),
  ].map(normalizarTexto);

  return (
    permisoDirecto ||
    campos.some((campo) =>
      permisosEnArreglo.includes(
        normalizarTexto(campo)
      )
    )
  );
};

const badgeEstado = (activo) => {
  return activo ? (
    <span className="badge rounded-pill text-bg-success">
      ACTIVO
    </span>
  ) : (
    <span className="badge rounded-pill text-bg-secondary">
      INACTIVO
    </span>
  );
};

/* =========================================================
   COMPONENTE
========================================================= */

const ListarConfiguracionCobros = () => {
  const navigate = useNavigate();

  const [configuraciones, setConfiguraciones] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [procesandoId, setProcesandoId] =
    useState(null);

  /* =======================================================
     AUTENTICACIÓN
  ======================================================= */

  const authConfig = useCallback(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);

  /* =======================================================
     PERMISOS
  ======================================================= */

  const moduloCobros = useMemo(
    () => obtenerModuloPorNombre(MODULO),
    []
  );

  const puedeLeer = useMemo(
    () =>
      tienePermiso(
        moduloCobros,
        "LEER"
      ),
    [moduloCobros]
  );

  const puedeCrear = useMemo(
    () =>
      tienePermiso(
        moduloCobros,
        "CREAR"
      ),
    [moduloCobros]
  );

  const puedeActualizar = useMemo(
    () =>
      tienePermiso(
        moduloCobros,
        "ACTUALIZAR"
      ),
    [moduloCobros]
  );

  const puedeEliminar = useMemo(
    () =>
      tienePermiso(
        moduloCobros,
        "ELIMINAR"
      ),
    [moduloCobros]
  );

  /* =======================================================
     MANEJO DE SESIÓN
  ======================================================= */

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");

          navigate("/", {
            replace: true,
          });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  /* =======================================================
     LISTAR CONFIGURACIONES
  ======================================================= */

  const getData = useCallback(async () => {
    if (!puedeLeer) {
      setConfiguraciones([]);
      return;
    }

    try {
      setLoading(true);

      const { data } =
        await axios.get(
          "/api/configuracioncobros/listall",
          authConfig()
        );

      if (
        !data ||
        !Array.isArray(
          data.configuraciones
        )
      ) {
        throw new Error(
          "Datos de configuración no válidos."
        );
      }

      setConfiguraciones(
        data.configuraciones
      );
    } catch (error) {
      console.error(
        "Error al cargar configuración de cobros:",
        error
      );

      if (
        manejarErrorSesion(error)
      ) {
        return;
      }

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudo cargar la configuración de cobros.",
        "error"
      );

      setConfiguraciones([]);
    } finally {
      setLoading(false);
    }
  }, [
    authConfig,
    manejarErrorSesion,
    puedeLeer,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  /* =======================================================
     NUEVA CONFIGURACIÓN
  ======================================================= */

  const nuevaConfiguracion = useCallback(
    () => {
      if (!puedeCrear) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para crear la configuración de cobros.",
          "warning"
        );

        return;
      }

      navigate(
        RUTAS_FRONTEND.nuevo
      );
    },
    [
      navigate,
      puedeCrear,
    ]
  );

  /* =======================================================
     EDITAR CONFIGURACIÓN
  ======================================================= */

  const editarConfiguracion = useCallback(
    (configuracion) => {
      if (!puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para actualizar la configuración de cobros.",
          "warning"
        );

        return;
      }

      navigate(
        RUTAS_FRONTEND.editar(
          configuracion._id
        )
      );
    },
    [
      navigate,
      puedeActualizar,
    ]
  );

  /* =======================================================
     ELIMINAR CONFIGURACIÓN
  ======================================================= */

  const eliminarConfiguracion =
    useCallback(
      async (configuracion) => {
        if (!puedeEliminar) {
          Swal.fire(
            "Sin permisos",
            "No tiene permisos para eliminar configuraciones.",
            "warning"
          );

          return;
        }

        const result =
          await Swal.fire({
            title:
              "¿Eliminar configuración?",
            text:
              "Esta acción eliminará la configuración seleccionada.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText:
              "Sí, eliminar",
            cancelButtonText:
              "Cancelar",
            confirmButtonColor:
              "#dc3545",
          });

        if (!result.isConfirmed) {
          return;
        }

        try {
          setProcesandoId(
            configuracion._id
          );

          const { data } =
            await axios.delete(
              `/api/configuracioncobros/delete/${configuracion._id}`,
              authConfig()
            );

          await Swal.fire({
            icon: "success",
            title:
              data?.message ||
              "Configuración eliminada",
            showConfirmButton: false,
            timer: 1600,
          });

          await getData();
        } catch (error) {
          console.error(
            "Error al eliminar configuración:",
            error
          );

          if (
            manejarErrorSesion(
              error
            )
          ) {
            return;
          }

          Swal.fire(
            "Error",
            error.response?.data
              ?.message ||
              "No se pudo eliminar la configuración.",
            "error"
          );
        } finally {
          setProcesandoId(null);
        }
      },
      [
        authConfig,
        getData,
        manejarErrorSesion,
        puedeEliminar,
      ]
    );

  /* =======================================================
     DATOS PARA DATATABLE
  ======================================================= */

  const dataTabla = useMemo(
    () =>
      configuraciones.map(
        (configuracion) => ({
          ...configuracion,

          avisos:
            Array.isArray(
              configuracion.diasAvisoVencimiento
            ) &&
            configuracion
              .diasAvisoVencimiento
              .length > 0
              ? configuracion.diasAvisoVencimiento.join(
                  ", "
                )
              : "—",

          gracia: `${
            configuracion.diasGraciaDefault ||
            0
          } día(s)`,

          periodoGracia:
            configuracion.permitirPeriodoGracia
              ? "Sí"
              : "No",

          bloqueo:
            configuracion.bloquearAlVencer
              ? "Sí"
              : "No",

          comprobante:
            configuracion.permitirCargaComprobante
              ? "Sí"
              : "No",

          aprobacionManual:
            configuracion.requiereAprobacionManualPago
              ? "Sí"
              : "No",

          estadoVisual:
            badgeEstado(
              configuracion.activo
            ),

          funciones: (
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              {puedeActualizar && (
                <button
                  className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 34,
                    height: 34,
                  }}
                  onClick={() =>
                    editarConfiguracion(
                      configuracion
                    )
                  }
                  title="Editar configuración"
                  type="button"
                  disabled={
                    loading ||
                    procesandoId ===
                      configuracion._id
                  }
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
              )}

              {puedeEliminar && (
                <button
                  className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 34,
                    height: 34,
                  }}
                  onClick={() =>
                    eliminarConfiguracion(
                      configuracion
                    )
                  }
                  title="Eliminar configuración"
                  type="button"
                  disabled={
                    loading ||
                    procesandoId ===
                      configuracion._id
                  }
                >
                  {procesandoId ===
                  configuracion._id ? (
                    <i className="fa-solid fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fa-solid fa-trash"></i>
                  )}
                </button>
              )}
            </div>
          ),
        })
      ),
    [
      configuraciones,
      eliminarConfiguracion,
      editarConfiguracion,
      loading,
      procesandoId,
      puedeActualizar,
      puedeEliminar,
    ]
  );

  /* =======================================================
     COLUMNAS
  ======================================================= */

  const columns = useMemo(
    () => [
      {
        Header: "Configuración",
        accessor:
          "nombreConfiguracion",
      },
      {
        Header: "Moneda",
        accessor:
          "monedaPrincipal",
      },
      {
        Header: "Avisos (días)",
        accessor: "avisos",
      },
      {
        Header: "Gracia",
        accessor: "gracia",
      },
      {
        Header:
          "Periodo gracia",
        accessor:
          "periodoGracia",
        hideHeaderOnMobile: true,
      },
      {
        Header:
          "Bloqueo al vencer",
        accessor: "bloqueo",
        hideHeaderOnMobile: true,
      },
      {
        Header:
          "Comprobante",
        accessor:
          "comprobante",
        hideHeaderOnMobile: true,
      },
      {
        Header:
          "Aprobación manual",
        accessor:
          "aprobacionManual",
        hideHeaderOnMobile: true,
      },
      {
        Header: "Estado",
        accessor:
          "estadoVisual",
      },
      {
        Header: "Acciones",
        accessor: "funciones",
      },
    ],
    []
  );

  /* =======================================================
     SIN PERMISO DE LECTURA
  ======================================================= */

  if (!puedeLeer) {
    return (
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-4">
          <div className="alert alert-warning mb-3">
            <i className="fa-solid fa-triangle-exclamation me-2"></i>
            No tiene permisos para consultar la configuración de cobros.
          </div>

          <button
            type="button"
            className="btn btn-dark"
            onClick={() =>
              navigate(
                RUTAS_FRONTEND.inicio
              )
            }
          >
            <i className="fa-solid fa-arrow-left me-2"></i>
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     VISTA
  ======================================================= */

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* ===============================================
              ENCABEZADO
          =============================================== */}

          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Configuración de cobros
                </h5>

                <small className="text-secondary">
                  Parámetros generales del módulo de pagos y suscripciones.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() =>
                    navigate(
                      RUTAS_FRONTEND.inicio
                    )
                  }
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {/* IMPORTANTE:
                    El botón depende únicamente del permiso CREAR.
                    Ya no depende de una segunda comprobación
                    esAdministrador.
                */}
                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={
                      nuevaConfiguracion
                    }
                    disabled={loading}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nueva
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ===============================================
              CONTENIDO
          =============================================== */}

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">
                  Cargando configuración…
                </div>
              </div>
            ) : dataTabla.length >
              0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={dataTabla}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5 px-3">
                <div className="display-6 mb-2">
                  ⚙️
                </div>

                <div className="fw-semibold">
                  No hay configuraciones registradas
                </div>

                <div className="small mb-3">
                  Cree la configuración general del módulo de cobros.
                </div>

                {/* IMPORTANTE:
                    Este botón también depende únicamente
                    del permiso CREAR.
                */}
                {puedeCrear ? (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={
                      nuevaConfiguracion
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear configuración
                  </button>
                ) : (
                  <div className="alert alert-warning d-inline-block mt-2 mb-0">
                    <i className="fa-solid fa-lock me-2"></i>
                    Tiene permiso para consultar, pero no para crear configuraciones.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarConfiguracionCobros;